import { toListHash } from '@upright/common/utils';

export enum CsrdMatterHierarchy {
  ESRS = 'E',
  UPRIGHT = 'U',
}

export interface SustainabilityMatter {
  id: SustainabilityMatterId;
  parent: SustainabilityMatterId | undefined; // undefined for top-level matters
  title: string;
  hierarchy: CsrdMatterHierarchy;
  qualifier?: string;
}

/**
 * List of all sustainabilility matters IDs, on all levels
 */
export enum SustainabilityMatterId {
  ENVIRONMENT = 'E',
  SOCIAL = 'S',
  GOVERNANCE = 'G',

  // E1 - climate change
  E1_CLIMATE_CHANGE = 'e1-cc', // FIXME: should be E1-cc, fixing needs a migration of db and input sheets
  // OA: The two below are flipped on the sheet. This is handled in code at convertLegacyMatterId.
  E1_CLIMATE_CHANGE_MITIGATION = 'E1-ccm',
  E1_CLIMATE_CHANGE_ADAPTATION = 'E1-cca',
  E1_ENERGY = 'E1-nrj',

  // E2 - pollution
  E2_POLLUTION = 'e1-pol', // FIXME: should be E2-pol, fixing needs a migration of db and input sheets
  E2_POLLUTION_OF_AIR = 'E2-poa',
  E2_POLLUTION_OF_WATER_AND_SOIL = 'E2-pws',
  E2_SUBSTANCES_OF_CONCERN = 'E2-soc',
  E2_SUBSTANCES_OF_VERY_HIGH_CONCERN = 'E2-sohc',
  E2_MICROPLASTICS = 'E2-mpl',

  // E3 - water and marine resources
  E3_WATER_AND_MARINE_RESOURCES = 'E3-wam',
  E3_WATER = 'E3-wat',
  E3_MARINE_RESOURCES = 'E3-mar',
  E3_WATER_WITHDRAWALS_AND_CONSUMPTION = 'E3-wwc',
  E3_WATER_DISCHARGES = 'E3-wdi',
  E3_EXTRACTION_AND_USE_OF_MARINE_RESOURCES = 'E3-emr',

  // E4 - biodiversity and ecosystems
  E4_BIODIVERSITY = 'E3-bdv', // FIXME: should be E4-bdv, fixing needs a migration of db and input sheets
  E4_DIRECT_DRIVERS_OF_BIODIVERSITY_LOSS = 'E3-ddb', // FIXME: should be E4-ddb, fixing needs a migration of db and input sheets
  E4_CLIMATE_CHANGE = 'E4-bcc',
  E4_LAND_USE_CHANGE_ETC = 'E4-luc',
  E4_DIRECT_EXPLOITATION = 'E4-dex',
  E4_INVASIVE_ALIEN_SPECIES = 'E4-aes',
  E4_POLLUTION = 'E4-pol',
  E4_ECOSYSTEMS_AND_SPECIES = 'E4-ecos',
  E4_ECOSYSTEM_SERVICES = 'E4-ess',

  // E5 - circular economy
  E5_CIRCULAR_ECONOMY = 'E5-cie',
  E5_RESOURCES_INFLOWS = 'E5-rin',
  E5_RESOURCE_OUTFLOWS_AND_WASTE = 'E5-row',

  // S1 - own workforce
  S1_OWN_WORKFORCE = 'S1-owf',
  S1_WORKING_CONDITIONS = 'S1-wco',
  S1_SECURE_EMPLOYMENT = 'S1-emp',
  S1_WORKING_TIME = 'S1-wtm',
  S1_ADEQUATE_WAGES = 'S1-wag',
  S1_SOCIAL_DIALOGUE = 'S1-dlg',
  S1_FREEDOM_OF_ASSOCIATION = 'S1-foa',
  S1_COLLECTIVE_BARGAINING = 'S1-cbg',
  S1_WORK_LIFE_BALANCE = 'S1-wlb',
  S1_HEALTH_AND_SAFETY = 'S1-has',
  S1_EQUAL_TREATMENT_AND_OPPOTUNITY_FOR_ALL = 'S1-eao',
  S1_GENDER_EQUALITY_AND_EQUAL_PAY = 'S1-geq',
  S1_TRAINING_AND_SKILLS = 'S1-tas',
  S1_EMPLOYMENT_AND_INCLUSION_OF_PERSONS_WITH_DISABILITIES = 'S1-ipd',
  S1_MEASURES_AGAINST_VIOLENCE_AND_HARRASMENT_IN_THE_WORKPLACE = 'S1-mvh',
  S1_DIVERSITY = 'S1-div',
  S1_OTHER_WORK_RELATED_RIGHTS = 'S1-owr',
  S1_CHILD_LABOUR = 'S1-chl',
  S1_FORCED_LABOUR = 'S1-fol',
  S1_ADEQUATE_HOUSING = 'S1-adh',
  S1_WATER_AND_SANITATION = 'S1-was',
  S1_PRIVACY = 'S1-prv',

  // S2 - workers in the value chain
  S2_WORKERS_IN_THE_VALUE_CHAIN = 'S2-wvc',
  S2_WORKING_CONDITIONS = 'S2-wco',
  S2_SECURE_EMPLOYMENT = 'S2-emp',
  S2_WORKING_TIME = 'S2-wtm',
  S2_ADEQUATE_WAGES = 'S2-wag',
  S2_SOCIAL_DIALOGUE = 'S2-dlg',
  S2_FREEDOM_OF_ASSOCIATION = 'S2-foa',
  S2_COLLECTIVE_BARGAINING = 'S2-cbg',
  S2_WORK_LIFE_BALANCE = 'S2-wlb',
  S2_HEALTH_AND_SAFETY = 'S2-has',
  S2_EQUAL_TREATMENT_AND_OPPORTUNITY_FOR_ALL = 'S2-eao',
  S2_GENDER_EQUALITY_AND_EQUAL_PAY = 'S2-geq',
  S2_TRAINING_AND_SKILLS = 'S2-tas',
  S2_EMPLOYMENT_AND_INCLUSION_OF_PERSONS_WITH_DISABILITIES = 'S2-ipd',
  S2_MEASURES_AGAINST_VIOLENCE_AND_HARRASMENT_IN_THE_WORKPLACE = 'S2-mvh',
  S2_DIVERSITY = 'S2-div',
  S2_OTHER_WORK_RELATED_RIGHTS = 'S2-owr',
  S2_CHILD_LABOUR = 'S2-chl',
  S2_FORCED_LABOUR = 'S2-fol',
  S2_ADEQUATE_HOUSING = 'S2-adh',
  S2_WATER_AND_SANITATION = 'S2-was',
  S2_PRIVACY = 'S2-prv',

  // S3 - affected communities
  S3_AFFECTED_COMMUNITIES = 'S3-aco',
  S3_COMMUNITIES_ECONOMIC_SOCIAL_AND_CULTURAL_RIGHTS = 'S3-esc',
  S3_ADEQUATE_HOUSING = 'S3-ahs',
  S3_ADEQUATE_FOOD = 'S3-afo',
  S3_WATER_AND_SANITATION = 'S3-was',
  S3_LAND_RELATED_IMPACTS = 'S3-lri',
  S3_SECURITY_RELATED_IMPACTS = 'S3-sri',
  S3_COMMUNITIES_CIVIL_AND_POLITICAL_RIGHTS = 'S3-ccr',
  S3_FREEDOM_OF_EXPRESSION = 'S3-foe',
  S3_FREEDOM_OF_ASSEMBLY = 'S3-foa',
  S3_IMPACT_ON_HUMAN_RIGHTS_DEFENDERS = 'S3-hrd',
  S3_RIGHTS_OF_INDIGENOUS_PEOPLES = 'S3-rip',
  S3_FREE_PRIOR_AND_INFORMED_CONSENT = 'S3-cst',
  S3_SELF_DETERMINATION = 'S3-sdt',
  S3_CULTURAL_RIGHTS = 'S3-cur',

  // S4 - consumers and end-users
  S4_CONSUMERS_AND_END_USERS = 'S4-cae',
  S4_INFORMATION_RELATED_IMPACTS_FOR_CONSUMERS_AND_END_USERS = 'S4-iri',
  S4_PRIVACY = 'S4-prv',
  S4_FREEDOM_OF_EXPRESSION = 'S4-foe',
  S4_ACCESS_TO_QUALITY_INFORMATION = 'S4-aqi',
  S4_PERSONAL_SAFETY_OF_CONSUMERS_END_USERS = 'S4-psc',
  S4_HEALTH_AND_SAFETY = 'S4-has',
  S4_SECURITY_OF_A_PERSON = 'S4-sop',
  S4_PROTECTION_OF_CHILDREN = 'S4-poc',
  S4_SOCIAL_INCLUSION_OF_CONSUMERS_END_USERS = 'S4-inc',
  S4_NON_DISCRIMINATION = 'S4-ndi',
  S4_ACCESS_TO_PRODUCTS_AND_SERVICES = 'S4-aps',
  S4_RESPONSIBLE_MARKETING_PRACTICES = 'S4-rmp',

  // G1 - corporate culture
  G1_BUSINESS_CONDUCT = 'G1-bco',
  G1_CORPORATE_CULTURE = 'G1-ccu',
  G1_PROTECTION_OF_WHISTEBLOWERS = 'G1-wbl',
  G1_ANIMAL_WELFARE = 'G1-anw',
  G1_POLITICAL_ENGAGEMENT = 'G1-poe',
  G1_MANAGEMENT_OF_RELATIONSHIPS_WITH_SUPPLIERS = 'G1-rws',
  G1_CORRUPTION_OF_BRIBERY = 'G1-cob',

  UPRIGHT_ENVIRONMENT = 'U-E',
  UPRIGHT_SOCIAL = 'U-S',
  UPRIGHT_KNOWLEDGE = 'U-K',
  UPRIGHT_HEALTH = 'U-H',
  UPRIGHT_JOBS = 'U-S2',
  UPRIGHT_TAXES = 'U-S1',
  UPRIGHT_SOCIETAL_INFRA = 'U-S3',
  UPRIGHT_SOCIETAL_STABILITY = 'U-S5',
  UPRIGHT_EQUALITY = 'U-S4',
  UPRIGHT_KNOWLEDGE_INFRA = 'U-K2',
  UPRIGHT_CREATES_KNOWLEDGE = 'U-K3',
  UPRIGHT_DISTRIBUTING_KNOWLEDGE = 'U-K4',
  UPRIGHT_SCARCE_HUMAN_CAPITAL = 'U-K1',
  UPRIGHT_PHYSICAL_DISEASES = 'U-H1',
  UPRIGHT_MENTAL_DISEASES = 'U-H2',
  UPRIGHT_NUTRITION = 'U-H3',
  UPRIGHT_RELATIONSHIPS = 'U-H4',
  UPRIGHT_MEANING_AND_JOY = 'U-H5',
  UPRIGHT_GHG_EMISSIONS = 'U-E1',
  UPRIGHT_NON_GHG_EMISSIONS = 'U-E2',
  UPRIGHT_SCARCE_NATURAL_RESOURCES = 'U-E3',
  UPRIGHT_BIODIVERSITY = 'U-E4',
  UPRIGHT_WASTE = 'U-E5',

  // The matteres have been separate and have been later merged, see documentation for more details
  // https://docs.google.com/document/d/1SNOGjMSp5sdZ_HNBEdZwIaPpaXMbwuI5_PD-6sY94iU/edit
  LEGACY_E5_RESOURCES_OUTFLOWS = 'E5-rou',
  LEGACY_E5_WASTE = 'E5-wst',

  LEGACY_E2_POLLUTION_OF_WATER = 'E2-pow',
  LEGACY_E2_POLLUTION_OF_SOIL = 'E2-pos',
  LEGACY_E2_POLLUTION_OF_LIVING_ORGANISMS_AND_FOOD_RESOURCES = 'E2-plo',

  LEGACY_E3_WATER_WITHDRAWALS = 'E3-wwi',
  LEGACY_E3_WATER_CONSUMPTION = 'E3-wco',

  LEGACY_E3_WATER_DISCHARGES_IN_THE_OCEANS = 'E3-wdio',

  LEGACY_E4_IMPACTS_ON_THE_STATE_OF_SPECIES = 'E4-iss',
  LEGACY_E4_IMPACTS_ON_THE_EXTENT_AND_CONDITION_OF_ECOSYSTEMS = 'E4-ces',
}

export const legacySustainabilitMatterIds = new Set([
  SustainabilityMatterId.LEGACY_E2_POLLUTION_OF_LIVING_ORGANISMS_AND_FOOD_RESOURCES,
  SustainabilityMatterId.LEGACY_E2_POLLUTION_OF_SOIL,
  SustainabilityMatterId.LEGACY_E2_POLLUTION_OF_WATER,
  SustainabilityMatterId.LEGACY_E3_WATER_CONSUMPTION,
  SustainabilityMatterId.LEGACY_E3_WATER_DISCHARGES_IN_THE_OCEANS,
  SustainabilityMatterId.LEGACY_E3_WATER_WITHDRAWALS,
  SustainabilityMatterId.LEGACY_E4_IMPACTS_ON_THE_EXTENT_AND_CONDITION_OF_ECOSYSTEMS,
  SustainabilityMatterId.LEGACY_E4_IMPACTS_ON_THE_STATE_OF_SPECIES,
  SustainabilityMatterId.LEGACY_E5_RESOURCES_OUTFLOWS,
  SustainabilityMatterId.LEGACY_E5_WASTE,
]);
export const allSustainabilityMatterIds = Object.values(SustainabilityMatterId).filter(
  id => !legacySustainabilitMatterIds.has(id),
);
export const allSustainabilityMatterIdsHash = toListHash(Object.values(SustainabilityMatterId));
